<template>
  <ConfirmDialog />
  <div class="mt-2">
    <div class="flex">
      <div
        class="flex flex-wrap mb-4"
        style="margin-left: auto;"
      >
        <SpmButton
          :label="t('vapidKeys.generate')"
          icon="far fa-plus-circle"
          class-style="p-button p-button-success"
          :loading="loading"
          @click="toggleGeneratePanel"
        />
      </div>
    </div>
    <div class="grid">
      <div class="col-12">
        <!-- VAPID Keys Section Card -->
        <div class="info-card mb-5">
          <div class="info-card__header">
            <div class="flex justify-content-between align-items-start">
              <div>
                <h2 class="info-card__title">
                  {{ t('vapidKeys.keysTitle') }}
                </h2>
                <p class="info-card__description">
                  {{ t('vapidKeys.subtitle') }}
                </p>
              </div>
              <SpmButton
                icon="fas fa-sync-alt"
                class-style="p-button-text p-button-rounded"
                :loading="loading"
                :title="t('common.refresh')"
                @click="loadVapidKeys"
              />
            </div>
          </div>
          <div class="info-card__body">
            <div class="vapid-key-list-container">
              <!-- Loading State -->
              <div
                v-if="loading && vapidKeys.length === 0"
                class="p-4 text-center"
              >
                <ProgressSpinner />
              </div>

              <!-- Empty State -->
              <div
                v-else-if="!loading && vapidKeys.length === 0"
                class="empty-state"
              >
                <i class="fas fa-bell-slash empty-state__icon" />
                <h3 class="empty-state__title">
                  {{ t('vapidKeys.empty.title') }}
                </h3>
                <p class="empty-state__description">
                  {{ t('vapidKeys.empty.description') }}
                </p>
                <SpmButton
                  :label="t('vapidKeys.generateFirst')"
                  icon="fas fa-plus"
                  class-style="p-button-success"
                  @click="toggleGeneratePanel"
                />
              </div>

              <!-- VAPID Keys List -->
              <div
                v-else
                class="vapid-key-list"
              >
                <div
                  v-for="key in vapidKeys"
                  :key="key.id"
                  class="vapid-key-item"
                  :class="{ 'vapid-key-item--default': key.is_default }"
                >
                  <div class="vapid-key-item__header">
                    <div class="vapid-key-item__name-group">
                      <i class="fas fa-key vapid-key-item__icon" />
                      <span class="vapid-key-item__name">{{ key.label || t('vapidKeys.unnamedKey') }}</span>
                      <Tag
                        v-if="key.is_default"
                        :value="t('vapidKeys.default')"
                        severity="success"
                        class="ml-2"
                      />
                    </div>
                    <div class="flex gap-2 align-items-center">
                      <SpmButton
                        v-if="!key.is_default"
                        :label="t('vapidKeys.setDefault')"
                        icon="fas fa-star"
                        class-style="p-button-text p-button-warning p-button-sm"
                        :loading="settingDefault === key.id"
                        @click="confirmSetDefault(key)"
                      />
                      <SpmButton
                        v-if="!key.is_default"
                        icon="fas fa-trash-alt"
                        class-style="p-button-text p-button-danger p-button-rounded"
                        :title="t('vapidKeys.delete')"
                        @click="confirmDelete(key)"
                      />
                    </div>
                  </div>
                  <div class="vapid-key-item__body">
                    <div class="vapid-key-item__public-key">
                      <span class="detail-group__label">{{ t('vapidKeys.publicKey') }}</span>
                      <div class="vapid-key-item__key-value">
                        <code>{{ key.vapid_public_key }}</code>
                        <SpmButton
                          icon="far fa-copy"
                          class-style="p-button-text p-button-secondary p-button-sm"
                          :title="t('common.copy')"
                          @click="copyPublicKey(key)"
                        />
                      </div>
                    </div>
                  <div class="vapid-key-item__public-key">
                      <span class="detail-group__label">{{ t('vapidKeys.privateKey') }}</span>
                      <div class="vapid-key-item__key-value">
                        <code>{{ key.vapid_private_key }}</code>
                        <SpmButton
                          icon="far fa-copy"
                          class-style="p-button-text p-button-secondary p-button-sm"
                          :title="t('common.copy')"
                          @click="copyPrivateKey(key)"
                        />
                      </div>
                    </div>
                    <div class="vapid-key-item__details">
                      <div class="detail-group">
                        <span class="detail-group__label">{{ t('vapidKeys.created') }}</span>
                        <div class="detail-group__value">
                          {{ new Date(key.date_creation).toLocaleDateString('fr-FR') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Generate Overlay Panel -->
        <SpmOverlayPanel
          ref="generatePanel"
          class-header="flex justify-content-between flex-wrap p-3 header-content"
          class-content="p-3"
          min-panel-width="400px"
          :close-panel="closeGeneratePanel"
          @onclose-panel="closeGeneratePanel = false"
        >
          <template #header>
            <div class="flex align-items-center">
              <i class="fas fa-key mr-2" />
              <span class="font-bold">{{ t('vapidKeys.generateModal.title') }}</span>
            </div>
          </template>

          <div class="field">
            <label
              for="keyLabel"
              class="block mb-2 font-medium"
            >{{ t('vapidKeys.generateModal.label') }}</label>
            <InputText
              id="keyLabel"
              v-model="generateForm.label"
              :placeholder="t('vapidKeys.generateModal.labelPlaceholder')"
              class="w-full"
              :class="{ 'p-invalid': v$.label.$error }"
              autofocus
              @blur="v$.label.$touch()"
            />
            <small
              v-if="v$.label.$error"
              class="p-error"
            >
              {{ v$.label.$errors[0].$message }}
            </small>
          </div>
          <div class="field">
            <label
              for="privateKeyLabel"
              class="block mb-2 font-medium"
            >{{ t('vapidKeys.generateModal.privateKeyLabel') }}</label>
            <InputText
              id="privateKeyLabel"
              v-model="generateForm.vapid_private_key"
              :placeholder="t('vapidKeys.generateModal.privateKeyPlaceholder')"
              class="w-full"
              :class="{ 'p-invalid': v$.vapid_private_key.$error }"
              @blur="v$.vapid_private_key.$touch()"
            />
            <small
              v-if="v$.vapid_private_key.$error"
              class="p-error"
            >
              {{ v$.vapid_private_key.$errors[0].$message }}
            </small>
          </div>
          <div class="field">
            <label
              for="publicKeyLabel"
              class="block mb-2 font-medium"
            >{{ t('vapidKeys.generateModal.publicKeyLabel') }}</label>
            <InputText
              id="publicKeyLabel"
              v-model="generateForm.vapid_public_key"
              :placeholder="t('vapidKeys.generateModal.publicKeyPlaceholder')"
              class="w-full"
              :class="{ 'p-invalid': v$.vapid_public_key.$error }"
              @blur="v$.vapid_public_key.$touch()"
            />
            <small
              v-if="v$.vapid_public_key.$error"
              class="p-error"
            >
              {{ v$.vapid_public_key.$errors[0].$message }}
            </small>
          </div>

          <template #footer>
            <div class="flex justify-content-end gap-2 p-2">
              <SpmButton
                :label="t('vapidKeys.generateModal.actions.generate')"
                icon="fas fa-magic"
                class-style="p-button-sm p-button-success"
                :loading="loading"
                @click="handleGenerateKey"
              />
            </div>
          </template>
        </SpmOverlayPanel>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import {
  defineComponent, onMounted, ref, computed,
} from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';
import ConfirmDialog from 'primevue/confirmdialog';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { UserState } from '@/composables/User';

interface VapidKey {
  id: string;
  label: string | null;
  vapid_public_key: string;
  vapid_private_key?: string;
  is_default: boolean;
  date_creation: string;
}

interface GenerateKeyRequest {
  label: string;
  vapid_private_key: string;
  vapid_public_key: string;
}

export default defineComponent({
  name: 'ShopVapidKeys',
  components: {
    SpmButton,
    Dialog,
    InputText,
    Tag,
    ProgressSpinner,
    ConfirmDialog,
    SpmOverlayPanel,
  },

  setup() {
    const { t } = useI18n();
    const confirm = useConfirm();

    const generatePanel = ref();
    const closeGeneratePanel = ref(false);
    const vapidKeys = ref<VapidKey[]>([]);
    const loading = ref(false);
    const settingDefault = ref<string | null>(null);
    const createdKey = ref<VapidKey | null>(null);

    const generateForm = ref<GenerateKeyRequest>({
      label: '',
      vapid_private_key: '',
      vapid_public_key: '',
    });

    // Validation rules for create form
    const rules = computed(() => ({
      label: {
        required: helpers.withMessage(
          () => t('vapidKeys.errors.labelRequired'),
          required,
        ),
      },
      vapid_private_key: {
        required: helpers.withMessage(
          () => t('vapidKeys.errors.privateKeyRequired'),
          required,
        ),
      },
      vapid_public_key: {
        required: helpers.withMessage(
          () => t('vapidKeys.errors.publicKeyRequired'),
          required,
        ),
      },
    }));

    const v$ = useVuelidate(rules, generateForm);

    const shopId = computed(() => UserState.activeShop?.id);

    const loadVapidKeys = async () => {
      if (!shopId.value) return;
      loading.value = true;
      try {
        const response = await nestCall('get', NestApiType.DEFAULT, `/shop/vapid-keys/${shopId.value}`);
        if (response?.success) {
          vapidKeys.value = response.data;
        } else {
          throw new Error(response?.error?.message || t('vapidKeys.errors.loadFailed'));
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        loading.value = false;
      }
    };

    const handleGenerateKey = async () => {
      if (!shopId.value) return;

      // Validate before submitting
      const isValid = await v$.value.$validate();
      if (!isValid) return;

      loading.value = true;
      try {
        const payload = {
          label: generateForm.value.label.trim() || null,
          vapid_private_key: generateForm.value.vapid_private_key.trim(),
          vapid_public_key: generateForm.value.vapid_public_key.trim(),
        };
        const response = await nestCall('post', NestApiType.DEFAULT, `/shop/vapid-keys/${shopId.value}`, {}, '', payload);
        if (response?.success) {
          closeGeneratePanel.value = true;
          createdKey.value = response.data;
          generateForm.value = {
            label: '',
            vapid_private_key: '',
            vapid_public_key: '',
          };
          v$.value.$reset();
          await loadVapidKeys();
          await showToastSuccess(t('vapidKeys.toasts.generated'));
        } else {
          const errorKey = response?.error || 'generateFailed';
          const errorMsg = t(`vapidKeys.errors.${errorKey}`) || t('vapidKeys.errors.generateFailed');
          throw new Error(errorMsg);
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        loading.value = false;
      }
    };

    const deleteKey = async (key: VapidKey) => {
      if (!shopId.value) return;
      try {
        const response = await nestCall('delete', NestApiType.DEFAULT, `/shop/vapid-keys/${shopId.value}/${key.id}`);
        if (response?.success) {
          vapidKeys.value = vapidKeys.value.filter((k) => k.id !== key.id);
          await showToastSuccess(t('vapidKeys.toasts.deleted'));
        } else {
          const errorKey = response?.error || 'deleteFailed';
          const errorMsg = t(`vapidKeys.errors.${errorKey}`) || t('vapidKeys.errors.deleteFailed');
          await showToastError(errorMsg);
        }
      } catch (err: any) {
        await showToastError(err.message);
      }
    };

    const setDefaultKey = async (key: VapidKey) => {
      if (!shopId.value) return;
      settingDefault.value = key.id;
      try {
        const response = await nestCall('put', NestApiType.DEFAULT, `/shop/vapid-keys/${shopId.value}/${key.id}/default`);
        if (response?.success) {
          await loadVapidKeys();
          await showToastSuccess(t('vapidKeys.toasts.defaultSet'));
        } else {
          throw new Error(response?.error || t('vapidKeys.errors.setDefaultFailed'));
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        settingDefault.value = null;
      }
    };

    const copyPublicKey = async (key: VapidKey) => {
      try {
        await navigator.clipboard.writeText(key.vapid_public_key);
        await showToastSuccess(t('vapidKeys.toasts.publicKeyCopied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const copyPrivateKey = async (key: VapidKey) => {
      if (!key.vapid_private_key) return;
      try {
        await navigator.clipboard.writeText(key.vapid_private_key);
        await showToastSuccess(t('vapidKeys.toasts.privateKeyCopied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const copyCreatedPublicKey = async () => {
      if (!createdKey.value) return;
      try {
        await navigator.clipboard.writeText(createdKey.value.vapid_public_key);
        await showToastSuccess(t('vapidKeys.toasts.publicKeyCopied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const copyCreatedPrivateKey = async () => {
      if (!createdKey.value?.vapid_private_key) return;
      try {
        await navigator.clipboard.writeText(createdKey.value.vapid_private_key);
        await showToastSuccess(t('vapidKeys.toasts.privateKeyCopied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const toggleGeneratePanel = (event: Event) => {
      generatePanel.value.toggle(event);
      closeGeneratePanel.value = false;
    };

    const confirmDelete = (key: VapidKey) => {
      confirm.require({
        message: t('vapidKeys.confirmDelete', { label: key.label || t('vapidKeys.unnamedKey') }),
        header: t('confirmation'),
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: t('yes'),
        acceptClass: 'p-button-danger',
        rejectLabel: t('no'),
        rejectClass: 'p-button-secondary',
        accept: () => {
          deleteKey(key);
        },
      });
    };

    const confirmSetDefault = (key: VapidKey) => {
      confirm.require({
        message: t('vapidKeys.confirmSetDefault', { label: key.label || t('vapidKeys.unnamedKey') }),
        header: t('confirmation'),
        icon: 'fas fa-star',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => {
          setDefaultKey(key);
        },
      });
    };

    onMounted(loadVapidKeys);

    return {
      t,
      v$,
      vapidKeys,
      loading,
      settingDefault,
      createdKey,
      generateForm,
      generatePanel,
      closeGeneratePanel,
      loadVapidKeys,
      handleGenerateKey,
      confirmDelete,
      confirmSetDefault,
      copyPublicKey,
      copyPrivateKey,
      copyCreatedPublicKey,
      copyCreatedPrivateKey,
      toggleGeneratePanel,
    };
  },
});
</script>

<style scoped lang="scss">
.vapid-key-success-modal {
  .success-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #f6c90e 0%, #e67e22 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
    margin: 0 auto;
    box-shadow: 0 4px 15px rgba(230, 126, 34, 0.3);
  }

  .vapid-key-label {
    display: block;
    font-weight: 600;
    color: var(--text-color);
    margin-bottom: 0.75rem;
    font-size: 1rem;
  }

  .vapid-key-input {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;

    .key-field {
      flex: 1;
      font-family: 'Courier New', monospace;
      font-size: 0.8rem;
      background: var(--surface-100);
      border: 2px solid var(--surface-300);
      padding: 0.75rem;
      border-radius: 6px;
    }
  }

  .key-info {
    background: var(--surface-50);
    border: 1px solid var(--surface-200);
    border-radius: 6px;
    padding: 1rem;

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0;

      &:not(:last-child) {
        border-bottom: 1px solid var(--surface-200);
      }

      .info-label {
        font-weight: 600;
        color: var(--text-color-secondary);
        font-size: 0.9rem;
      }

      .info-value {
        font-weight: 500;
        color: var(--text-color);
        font-size: 0.9rem;
      }
    }
  }

  .copy-warning {
    display: block;
    margin-top: 0.4rem;
    color: #856404;
    background: #fff3cd;
    border-left: 4px solid #ffc107;
    padding: 0.4rem 0.6rem;
    border-radius: 4px;
    font-size: 0.82rem;
  }

  .modal-footer {
    display: flex;
    justify-content: center;
    padding-top: 0.5rem;
  }
}
</style>

<style scoped lang="scss">
.info-card {
  background-color: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);

  &__header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--surface-border);
  }

  &__title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    text-align: left;
  }

  &__body {
    padding: 1.25rem;
  }

  &__description {
    color: var(--text-color-secondary);
    margin: 0 0 0rem;
    font-size: 0.9rem;
    max-width: 700px;
    text-align: left;
  }
}

.vapid-page-title {
  margin: 0;
  text-align: left;
}

.vapid-key-list-container {
  background-color: var(--surface-card);
}

.vapid-key-list {
  display: flex;
  flex-direction: column;
}

.vapid-key-item {
  padding: 1.25rem;
  border-bottom: 1px solid var(--surface-border);
  transition: background-color 0.2s;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--surface-hover);
  }

  &--default {
    border-left: 3px solid var(--green-500);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__name-group {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  &__icon {
    color: var(--primary-color);
  }

  &__name {
    font-size: 1.1rem;
    font-weight: 600;
  }

  &__public-key {
    margin-bottom: 1rem;
    text-align: left;

    .vapid-key-item__key-value {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background-color: var(--surface-50);
      border: 1px solid var(--surface-200);
      padding: 0.5rem 0.75rem;
      border-radius: var(--border-radius);
      margin-top: 0.25rem;

      code {
        flex: 1;
        word-break: break-all;
        font-size: 0.8rem;
        color: var(--text-color-secondary);
        font-family: var(--font-family-monospace);
      }
    }
  }

  &__details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.25rem;
    text-align: left;
  }
}

.detail-group {
  &__label {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    margin-bottom: 0.25rem;
    text-transform: uppercase;
    display: block;
    text-align: left;
  }

  &__value {
    font-weight: 500;
    text-align: left;
  }
}

.empty-state {
  padding: 3rem;
  text-align: center;

  &__icon {
    font-size: 3rem;
    color: var(--text-color-secondary);
    margin-bottom: 1.5rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  &__description {
    color: var(--text-color-secondary);
    margin-bottom: 1.5rem;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
